import React, { useEffect, useState } from "react";
import { db, auth } from "./firebase";
import { collection, getDocs } from "firebase/firestore";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Tabs,
  Tab,
  Avatar,
  Chip,
  Stack,
  Grid,
  CircularProgress
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

function arrayOverlap(arr1, arr2) {
  return arr1.some((v) => arr2.includes(v));
}

export default function MatchesDashboard() {
  const [tab, setTab] = useState("helpme");
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;
      const snap = await getDocs(collection(db, "users"));
      const allUsers = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      const me = allUsers.find((u) => u.id === user.uid);
      setMyProfile(me);
      setUsers(allUsers.filter((u) => u.id !== user.uid));
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (!myProfile) return <Typography color="error">Could not load your profile.</Typography>;

  // Who Can Help Me: users who offer what I want
  const helpMe = users.filter((u) =>
    arrayOverlap(u.skillsOffered || [], myProfile.skillsWanted || [])
  );

  // Mutual Exchange: both can help each other
  const mutual = users.filter(
    (u) =>
      arrayOverlap(u.skillsOffered || [], myProfile.skillsWanted || []) &&
      arrayOverlap(myProfile.skillsOffered || [], u.skillsWanted || [])
  );

  function renderUser(u) {
    const offerMatch = (u.skillsOffered || []).filter((s) => (myProfile.skillsWanted || []).includes(s));
    const wantMatch = (myProfile.skillsOffered || []).filter((s) => (u.skillsWanted || []).includes(s));
    return (
      <Card key={u.id} sx={{ mb: 2, borderRadius: 3, boxShadow: 2 }}>
        <CardHeader
          avatar={<Avatar>{(u.calledName || u.emailId || u.email || '?')[0].toUpperCase()}</Avatar>}
          title={<Typography variant="subtitle1">{u.calledName || u.emailId || u.email || '(not provided)'}</Typography>}
          subheader={u.country || u.timeZone || ''}
        />
        <CardContent>
          <Stack spacing={1}>
            {u.whatsapp && <Typography variant="body2"><b>WhatsApp:</b> {u.whatsapp}</Typography>}
            {u.telegram && <Typography variant="body2"><b>Telegram:</b> {u.telegram}</Typography>}
            <Typography variant="body2"><b>They offer:</b> {(u.skillsOffered || []).map(s => <Chip key={s} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} />)}</Typography>
            <Typography variant="body2"><b>They want:</b> {(u.skillsWanted || []).map(s => <Chip key={s} label={s} size="small" color="warning" sx={{ mr: 0.5, mb: 0.5 }} />)}</Typography>
            {offerMatch.length > 0 && (
              <Typography variant="body2" color="success.main">
                <b>Matched (they offer):</b> {offerMatch.map(s => <Chip key={s} label={s} size="small" color="success" sx={{ mr: 0.5 }} />)}
              </Typography>
            )}
            {wantMatch.length > 0 && (
              <Typography variant="body2" color="primary.main">
                <b>Matched (they want):</b> {wantMatch.map(s => <Chip key={s} label={s} size="small" color="primary" sx={{ mr: 0.5 }} />)}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, margin: '40px auto', p: 2 }}>
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
        <CardHeader
          avatar={<GroupIcon color="primary" sx={{ fontSize: 40 }} />}
          title={<Typography variant="h5">Skill Exchange Dashboard</Typography>}
        />
        <CardContent>
          <Tabs
            value={tab}
            onChange={(_, val) => setTab(val)}
            indicatorColor="primary"
            textColor="primary"
            sx={{ mb: 2 }}
          >
            <Tab label="Who Can Help Me" value="helpme" icon={<GroupIcon />} iconPosition="start" />
            <Tab label="Mutual Exchange" value="mutual" icon={<SwapHorizIcon />} iconPosition="start" />
          </Tabs>
          {tab === "helpme" && (
            <Box>
              {helpMe.length === 0 ? (
                <Typography>No users found who offer your wanted skills.</Typography>
              ) : (
                helpMe.map(renderUser)
              )}
            </Box>
          )}
          {tab === "mutual" && (
            <Box>
              {mutual.length === 0 ? (
                <Typography>No mutual matches found.</Typography>
              ) : (
                mutual.map(renderUser)
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
